import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWith409 from './baseQueryWith409';
import qs from 'qs';
import fetch from 'auth/FetchInterceptor';

// direct fetch without RTK Query
export const getConsentimientosDatosPersonales = query =>
	fetch.get(
		`/consentimientos-datos-personales${
			query
				? `?${qs.stringify(
						{ ...query, publicationState: 'live' },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);

export const getConsentimientoDatosPersonales = (id, query) =>
	fetch.get(
		`/consentimientos-datos-personales/${id}${
			query
				? `?${qs.stringify(
						{ ...query, publicationState: 'live' },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);

export const addConsentimientoDatosPersonales = data =>
	fetch.post('/consentimientos-datos-personales', data);

export const updateConsentimientoDatosPersonales = ({ id, ...data }) =>
	fetch.put(`/consentimientos-datos-personales/${id}`, data);

export const deleteConsentimientoDatosPersonales = id =>
	fetch.delete(`/consentimientos-datos-personales/${id}`);

// RTK Query
export const consentimientosDatosPersonalesApi = createApi({
	reducerPath: 'consentimientosDatosPersonalesApi',
	baseQuery: baseQueryWith409,

	// default configuration
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: true,
	refetchOnFocus: true,

	//endpoints
	endpoints: build => ({
		getConsentimientosDatosPersonales: build.query({
			query: query => {
				return {
					url: `/consentimientos-datos-personales${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			transformResponse: response => response.data,
			providesTags: result =>
				// is result available?
				result
					? // successful query
					  [
							...result.map(({ id }) => ({
								type: 'ConsentimientosDatosPersonales',
								id,
							})),
							{ type: 'ConsentimientosDatosPersonales', id: 'LIST' },
					  ]
					: // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
					  [{ type: 'ConsentimientosDatosPersonales', id: 'LIST' }],
		}),

		getConsentimientoDatosPersonales: build.query({
			query: ({ id, query }) => {
				return {
					url: `/consentimientos-datos-personales/${id}${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			transformResponse: response => response.data,
			providesTags: (result, error, id) => [
				{ type: 'ConsentimientosDatosPersonales', id },
			],
		}),

		getCurrentUserConsentimiento: build.query({
			query: query => {
				return {
					url: `/consentimientos-datos-personales${`?${qs.stringify(
						{
							filters: {
								users_permissions_user: {
									$eq: '$currentUserId', // Esto se reemplazará en el backend
								},
							},
							sort: ['createdAt:desc'],
							pagination: {
								page: 1,
								pageSize: 1,
							},
							populate: [
								'plantilla_consentimiento',
								'users_permissions_user',
							],
							...(query ? query : {}),
							publicationState: 'live',
						},
						{
							encodeValuesOnly: true,
						}
					)}`}`,
					method: 'GET',
				};
			},
			transformResponse: response =>
				response.data.length ? response.data[0] : null,
			providesTags: () => [
				{ type: 'ConsentimientosDatosPersonales', id: 'CURRENT_USER' },
			],
		}),

		addConsentimientoDatosPersonales: build.mutation({
			query: body => ({
				url: `/consentimientos-datos-personales`,
				method: 'POST',
				body: { data: body },
			}),
			transformResponse: response => response.data,
			invalidatesTags: [
				{ type: 'ConsentimientosDatosPersonales', id: 'LIST' },
				{ type: 'ConsentimientosDatosPersonales', id: 'CURRENT_USER' },
			],
		}),

		updateConsentimientoDatosPersonales: build.mutation({
			query: ({ id, ...body }) => ({
				url: `/consentimientos-datos-personales/${id}`,
				method: 'PUT',
				body: { data: body },
			}),
			transformResponse: response => response.data,
			invalidatesTags: (result, error, { id }) => [
				{ type: 'ConsentimientosDatosPersonales', id },
				{ type: 'ConsentimientosDatosPersonales', id: 'LIST' },
				{ type: 'ConsentimientosDatosPersonales', id: 'CURRENT_USER' },
			],
		}),

		deleteConsentimientoDatosPersonales: build.mutation({
			query: id => ({
				url: `/consentimientos-datos-personales/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: (result, error, id) => [
				{ type: 'ConsentimientosDatosPersonales', id },
				{ type: 'ConsentimientosDatosPersonales', id: 'LIST' },
				{ type: 'ConsentimientosDatosPersonales', id: 'CURRENT_USER' },
			],
		}),

		// ENDPOINT PARA ACEPTAR CONSENTIMIENTO
		acceptConsent: build.mutation({
			query: ({ plantillaId, usuarioId, metadata = {} }) => ({
				url: `/consentimientos-datos-personales`,
				method: 'POST',
				body: {
					data: {
						accion: 'acepta',
						plantilla: plantillaId,
						usuario: usuarioId,
						metadata: {
							user_agent: navigator.userAgent,
							timestamp: new Date().toISOString(),
							source: 'ortuzar_web_application',
							version: 'beta_1.0',
							...metadata,
						},
					},
				},
			}),
			transformResponse: response => response.data,
			invalidatesTags: [
				{ type: 'ConsentimientosDatosPersonales', id: 'LIST' },
				{ type: 'ConsentimientosDatosPersonales', id: 'CURRENT_USER' },
			],
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					// Aquí puedes agregar lógica adicional después de aceptar
					console.log('Consentimiento aceptado exitosamente');
				} catch (error) {
					console.error('Error al aceptar consentimiento:', error);
				}
			},
		}),

		// ENDPOINT PARA RECHAZAR CONSENTIMIENTO
		rejectConsent: build.mutation({
			query: ({
				plantillaId,
				usuarioId,
				razon_rechazo = 'Usuario rechazó política',
				metadata = {},
			}) => ({
				url: `/consentimientos-datos-personales`,
				method: 'POST',
				body: {
					data: {
						accion: 'rechaza',
						plantilla: plantillaId,
						usuario: usuarioId,
						metadata: {
							user_agent: navigator.userAgent,
							timestamp: new Date().toISOString(),
							source: 'ortuzar_web_application',
							version: 'beta_1.0',
							...metadata,
						},
					},
				},
			}),
			transformResponse: response => response.data,
			invalidatesTags: [
				{ type: 'ConsentimientosDatosPersonales', id: 'LIST' },
				{ type: 'ConsentimientosDatosPersonales', id: 'CURRENT_USER' },
			],
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					// Aquí puedes agregar lógica para el rechazo (ej: logout, limpieza de datos)
					console.log(
						'Consentimiento rechazado - iniciando proceso de deshabilitación'
					);

					// Ejemplo: dispatch de una acción para limpiar el store
					// dispatch(clearUserData());

					// Ejemplo: redireccionar a página de despedida
					// window.location.href = '/account-disabled';
				} catch (error) {
					console.error('Error al rechazar consentimiento:', error);
				}
			},
		}),

		// ENDPOINT PARA REVOCAR CONSENTIMIENTO (para uso futuro)
		revokeConsent: build.mutation({
			query: ({
				consentimientoId,
				razon_revocacion = 'Revocación solicitada por usuario',
			}) => ({
				url: `/consentimientos-datos-personales/${consentimientoId}`,
				method: 'PUT',
				body: {
					data: {
						estado: 'revocado',
						fecha_revocacion: new Date().toISOString(),
						razon_revocacion,
						metadata_revocacion: {
							timestamp: new Date().toISOString(),
							source: 'web_application',
							action: 'revoke',
						},
					},
				},
			}),
			transformResponse: response => response.data,
			invalidatesTags: (result, error, { consentimientoId }) => [
				{ type: 'ConsentimientosDatosPersonales', id: consentimientoId },
				{ type: 'ConsentimientosDatosPersonales', id: 'LIST' },
				{ type: 'ConsentimientosDatosPersonales', id: 'CURRENT_USER' },
			],
		}),
	}),
});

export const {
	useGetConsentimientosDatosPersonalesQuery,
	useGetConsentimientoDatosPersonalesQuery,
	useGetCurrentUserConsentimientoQuery,
	useAddConsentimientoDatosPersonalesMutation,
	useUpdateConsentimientoDatosPersonalesMutation,
	useDeleteConsentimientoDatosPersonalesMutation,
	useAcceptConsentMutation,
	useRejectConsentMutation,
	useRevokeConsentMutation,
} = consentimientosDatosPersonalesApi;

export default consentimientosDatosPersonalesApi;
